import type { NavigationItem } from '@dealerportal/common';
import React from 'react';
import type { Attribs } from './Include';

type Props = {
  itemsinrow: number;
  id: string;
  className: string;
  showatstart: string;
  columnscount: number;
  showpromotedproduct: number;
  megamenusubmenulayout: number;
  showproductname: number;
  showproductshortdescription: boolean;
  useablock: boolean;
  maxwidth: string;
  sitemapstartlevel: string;
  sitemapentrypoint: string;
  sitemapdepth: string;
  elementsToRender: Element[];
  menuItems: NavigationItem[];
};

type EnhancedElement = Element & {
  props?: {
    attribs?: Attribs;
    className?: string;
  };
};

type ListItemProps = {
  href?: string;
  text?: string;
  className?: string;
};

export function ListItem(props: ListItemProps) {
  return (
    <li>
      <a
        key={props.href}
        role="menuitem"
        href={props.href}
        className={`ui-block ${props.className} ${props.className}_item`}
      >
        <div className={`${props.className}_txt`}>{props.text}</div>
      </a>
    </li>
  );
}

export function DynamicMenu(props: Props) {
  const { menuItems, sitemapstartlevel, sitemapentrypoint, sitemapdepth } =
    props;

  const entryPoints = sitemapentrypoint
    .split(':')
    .map((i) => Number.parseInt(i, 10));

  let item = menuItems.filter(({ position }) => position === entryPoints[1])[0];

  if (!item) return null;

  if (entryPoints.length > 2 && item?.children) {
    item = item.children[entryPoints[2]];
  }

  function shouldShow({ hideInMenu, isVisible }: NavigationItem) {
    return !hideInMenu && isVisible;
  }

  return (
    <>
      {sitemapstartlevel === '0' && (
        <>
          <ListItem
            href={item.alias}
            text={item.name}
            className={props.className}
          />

          {Number.parseInt(sitemapdepth, 10) > 1 &&
            item.children &&
            item.children.filter(shouldShow).map((child: NavigationItem) => (
              <React.Fragment key={child.alias}>
                <ListItem
                  href={child.alias}
                  text={child.name}
                  className={props.className}
                />

                {Number.parseInt(sitemapdepth, 10) > 2 &&
                  child.children &&
                  child.children
                    .filter(shouldShow)
                    .map((grandchild: NavigationItem) => (
                      <ListItem
                        key={grandchild.alias}
                        href={grandchild.alias}
                        text={grandchild.name}
                        className={props.className}
                      />
                    ))}
              </React.Fragment>
            ))}
        </>
      )}

      {sitemapstartlevel === '1' &&
        item.children &&
        item.children.filter(shouldShow).map((child: NavigationItem) => (
          <React.Fragment key={child.alias}>
            <ListItem
              href={child.alias}
              text={child.name}
              className={props.className}
            />

            {Number.parseInt(sitemapdepth, 10) > 1 &&
              child.children &&
              child.children
                .filter(shouldShow)
                .map((grandchild: NavigationItem) => (
                  <ListItem
                    key={grandchild.alias}
                    href={grandchild.alias}
                    text={grandchild.name}
                    className={props.className}
                  />
                ))}
          </React.Fragment>
        ))}
    </>
  );
}

export function VerticalMultiMenu(props: Props) {
  const isDynamic = props.sitemapentrypoint.includes('p:');
  const { elementsToRender } = props;
  return (
    <nav>
      <ul
        id={props.id}
        className={`${props.className} !ui-list-none !ui-p-0 !ui-m-0`}
      >
        {isDynamic ? (
          <DynamicMenu {...props} />
        ) : (
          Array.isArray(elementsToRender) &&
          (elementsToRender as EnhancedElement[])
            .filter(({ props }) => props?.attribs)
            .map((item) => {
              if (!item.props?.attribs) return null;
              const { href = '', text = '' } = item.props.attribs;

              return (
                <ListItem
                  key={href}
                  href={href}
                  text={text}
                  className={props?.className}
                />
              );
            })
        )}
      </ul>
    </nav>
  );
}

export default VerticalMultiMenu;
